import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import matchingService from '@/services/matchingService'

/**
 * Query key factory for AI matching queries
 */
export const aiMatchingKeys = {
  all: ['aiMatching'] as const,
  matchResults: (matchId: string, page: number, minScore?: number, excludeProspects?: boolean) =>
    [...aiMatchingKeys.all, 'results', matchId, page, minScore, excludeProspects] as const,
}

/**
 * Hook to fetch match results for a specific match
 *
 * Features:
 * - Auto-refetches when filters change
 * - Caches results per page and score
 * - Handles errors gracefully
 *
 * @param matchId - The match ID to fetch results for
 * @param page - The page number (default: 1)
 * @param minScore - The minimum score filter (optional)
 * @param enabled - Whether to fetch (default: true)
 * @returns Query result with match results, loading state, and error state
 */
export function useMatchResults(
  matchId: string | undefined,
  page: number = 1,
  minScore?: number,
  enabled: boolean = true,
  excludeProspects?: boolean
) {
  return useQuery({
    queryKey: aiMatchingKeys.matchResults(matchId || '', page, minScore, excludeProspects),
    queryFn: async () => {
      if (!matchId) throw new Error('Match ID is required')

      const results = await matchingService.getMatchResults({
        matchId,
        page,
        limit: 20,
        minScore,
        excludeProspects,
      })

      // Sort results so archived items appear last
      return {
        ...results,
        results: results.results.sort((a, b) => {
          if (a.archived && !b.archived) return 1
          if (!a.archived && b.archived) return -1
          return (a.rankPosition || 0) - (b.rankPosition || 0)
        }),
      }
    },
    enabled: enabled && !!matchId,
    staleTime: 60000, // 1 minute
    retry: 2,
    refetchOnWindowFocus: false,
  })
}

/**
 * Hook to submit feedback for a match result
 *
 * Features:
 * - Automatically invalidates relevant caches
 * - Shows success/error toasts
 * - Supports optimistic updates
 *
 * @returns Mutation object with mutate function and state
 */
export function useSubmitFeedback() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      matchId,
      candidateId,
      feedback,
      notes,
    }: {
      matchId: string
      candidateId: string
      feedback: 'RELEVANT' | 'NOT_RELEVANT' | 'SHORTLISTED'
      notes?: string
    }) => matchingService.submitFeedback(matchId, candidateId, { feedback, notes }),

    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: aiMatchingKeys.all })

      if (variables.feedback === 'SHORTLISTED') {
        queryClient.invalidateQueries({ queryKey: ['saved-candidates'] })
      }
    },
  })
}

/**
 * Hook to archive a match result
 *
 * Features:
 * - Automatically refreshes match results
 * - Shows success/error toasts
 *
 * @returns Mutation object with mutate function and state
 */
export function useArchiveMatch() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (matchResultId: string) => matchingService.archiveMatchResult(matchResultId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: aiMatchingKeys.all })
    },
  })
}

/**
 * Hook to delete a match result
 *
 * Features:
 * - Automatically refreshes match results
 * - Shows success/error toasts
 *
 * @returns Mutation object with mutate function and state
 */
export function useDeleteMatch() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (matchResultId: string) => matchingService.deleteMatchResult(matchResultId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: aiMatchingKeys.all })
    },
  })
}
